// Bounded historical trace observations for independently selected direct-storage
// profiles, retaining the controlled ForkAsset compatibility entrypoint.
// This is not token admission, signer/owner proof, or financial authority.
import {
  decodeEventLog,
  decodeFunctionData,
  encodeAbiParameters,
  encodeEventTopics,
  encodeFunctionData,
  getAddress,
  isAddressEqual,
  keccak256,
  parseAbiParameters,
  parseAbi,
  stringToHex,
  type Address,
  type Hex,
  type PublicClient,
} from 'viem';
import { entryPoint07Abi, getUserOperationHash, type UserOperation } from 'viem/account-abstraction';
import { networkBuild } from '@hoodmorning/config';
import type { decodeAndValidateNativeV2Trade } from './nativeRouterTradeDecoder.js';
import type { NativeWrapperObservationClient, NativeWrapperObservationProfile } from './nativeWrapperObservation.js';
import { assertDirectNativeWrapperRuntime, directNativeWrapperObservationAbi } from './directNativeWrapperObservation.js';
import type { SmaBDeployedUserOpValidationTraceInput } from './smaBFirstUserOpValidationTrace.js';

const MAX_TRACE_BYTES = 2 * 1024 * 1024;
const MAX_TRACE_ACCOUNTS = 128;
const MAX_STORAGE_SLOTS = 512;
const MAX_TRACE_FRAMES = 2_048;
const MAX_TRACE_DEPTH = 64;
const MAX_TRACE_NODES = 20_000;
const MAX_CALLDATA_BYTES = 256 * 1024;
const ZERO_ADDRESS = '0x' + '0'.repeat(40);
const ENTRY_POINT_V07 = '0x0000000071727de22e5e9d8baf0edac6f37da032';
const textEncoder = new TextEncoder();

type TraceObject = Readonly<Record<string, unknown>>;

type AuthorityFlags = Readonly<{
  executionAuthorized: false;
  resubmissionAuthorized: false;
  movementPublicationAuthorized: false;
  accountingEligible: false;
  feeSettlementAuthorized: false;
  pnlPublicationAuthorized: false;
  referralSettlementAuthorized: false;
  pointsPublicationAuthorized: false;
  executionTimeOwnerVerified: false;
}>;

const flags: AuthorityFlags = Object.freeze({
  executionAuthorized: false,
  resubmissionAuthorized: false,
  movementPublicationAuthorized: false,
  accountingEligible: false,
  feeSettlementAuthorized: false,
  pnlPublicationAuthorized: false,
  referralSettlementAuthorized: false,
  pointsPublicationAuthorized: false,
  executionTimeOwnerVerified: false,
});

export type ExternalTerminalSettlementTraceClient = Readonly<Pick<PublicClient,
  'getChainId' | 'getTransaction' | 'getTransactionReceipt' | 'getBlock' | 'getBytecode'>>;

/** Local-only debug RPC port. It is deliberately separate from the public client. */
export type LocalTracePort = (
  method: 'eth_chainId' | 'debug_traceTransaction',
  params: unknown[],
  timeoutMs: number,
) => Promise<unknown>;

export type ExternalTerminalSettlementTraceInput = Readonly<{
  local: LocalTracePort;
  client: ExternalTerminalSettlementTraceClient;
  transactionHash: string;
  vault: string;
  tokenIn: string;
  tokenOut: string;
  treasury: string;
  expected: Readonly<{ amountIn: bigint; netOutput: bigint; fee: bigint }>;
  tokenRuntimeHash: string;
}>;

/** Independently selected semantic configuration, not a portable proof or an
 * admission API. Consumers must resolve it from reviewed release/source facts. */
export type DirectStorageTokenProfile = Readonly<{
  schemaVersion: 1;
  kind: 'direct-erc20-storage-v1';
  token: Address;
  runtimeHash: Hex;
  templateHash: Hex;
  decimals: number;
  balanceMappingSlot: string;
  familyId: string;
}>;

export type ProfiledTerminalSettlementTraceInput = Readonly<
  Omit<ExternalTerminalSettlementTraceInput, 'tokenIn' | 'tokenOut' | 'tokenRuntimeHash'> & {
    tokenInProfile: DirectStorageTokenProfile;
    tokenOutProfile: DirectStorageTokenProfile;
  }>;

export type ProfiledTerminalSettlementTraceResult = Readonly<
  Omit<ExternalTerminalSettlementTraceResult, 'scope' | 'assets'> & {
    scope: 'direct-storage-profile-transaction-trace-deltas-only';
    assets: Readonly<{ vault: Address; treasury: Address;
      tokenInProfile: DirectStorageTokenProfile; tokenOutProfile: DirectStorageTokenProfile }>;
  }>;

export type ExternalTerminalSettlementTraceCall = Readonly<{
  type: string;
  from: Address;
  to: Address;
  gas: string | null;
  gasUsed: string | null;
  inputSelector: Hex | null;
  inputHash: Hex;
  outputHash: Hex;
  reverted: boolean;
  calls: readonly ExternalTerminalSettlementTraceCall[];
}>;

export type ExternalTerminalSettlementTraceBalance = Readonly<{
  before: string;
  after: string;
  delta: string;
  preSlotPresent: boolean;
  postSlotPresent: boolean;
}>;

export type ExternalTerminalSettlementTraceResult = Readonly<{
  schemaVersion: 1;
  scope: 'local-forkasset-transaction-trace-deltas-only';
  chainId: number;
  source: Readonly<{
    transactionHash: Hex;
    blockHash: Hex;
    blockNumber: string;
    transactionIndex: number;
    entryPoint: Address;
    inputHash: Hex;
    userOperationHash: Hex;
    nonce: string;
    traceCommitments: Readonly<{ fullPrestate: Hex; diff: Hex; call: Hex }>;
    traceProfile: Readonly<{
      prestate: 'prestateTracer';
      diff: 'prestateTracer.diffMode';
      call: 'callTracer.withLog';
    }>;
    callTrace: ExternalTerminalSettlementTraceCall;
    traceNotCryptographicProof: true;
  }>;
  assets: Readonly<{
    tokenIn: Address;
    tokenOut: Address;
    treasury: Address;
    vault: Address;
    runtimeHash: Hex;
    decimals: 6;
    balanceMappingSlot: '0';
  }>;
  deltas: Readonly<{
    tokenInVault: ExternalTerminalSettlementTraceBalance;
    tokenOutVault: ExternalTerminalSettlementTraceBalance;
    tokenOutTreasury: ExternalTerminalSettlementTraceBalance;
  }>;
  traceBounds: Readonly<{
    maxBytesPerTrace: number;
    maxAccounts: number;
    maxStorageSlotsPerAccount: number;
    maxFrames: number;
    maxDepth: number;
    observedFrames: number;
  }>;
  limitations: readonly string[];
}> & AuthorityFlags;

type TraceTransaction = Awaited<ReturnType<ExternalTerminalSettlementTraceClient['getTransaction']>>;
type TraceReceipt = Awaited<ReturnType<ExternalTerminalSettlementTraceClient['getTransactionReceipt']>>;
type TraceBlock = Awaited<ReturnType<ExternalTerminalSettlementTraceClient['getBlock']>>;
type TraceSnapshot = Readonly<{
  transaction: TraceTransaction & Readonly<{ blockNumber: bigint }>;
  receipt: TraceReceipt;
  block: TraceBlock & Readonly<{ hash: Hex }>;
}>;

function fail(message: string): never {
  throw new Error('local-settlement-trace: ' + message);
}

function ownObject(value: unknown, label: string): TraceObject {
  if (!value || typeof value !== 'object' || Array.isArray(value) || Object.getPrototypeOf(value) !== Object.prototype) {
    fail(label + ' must be an object');
  }
  return value as TraceObject;
}

function hex(value: unknown, label: string, bytes: number | undefined = undefined): Hex {
  if (typeof value !== 'string' || !/^0x[0-9a-fA-F]*$/.test(value) || value.length % 2 !== 0) fail('invalid ' + label);
  if (bytes !== undefined && value.length !== 2 + bytes * 2) fail('invalid ' + label + ' length');
  return value.toLowerCase() as Hex;
}

function address(value: unknown, label: string): Address {
  try {
    return getAddress(hex(value, label, 20));
  } catch {
    return fail('invalid ' + label);
  }
}

function quantity(value: unknown, label: string): bigint {
  if (typeof value !== 'string' || !/^0x[0-9a-fA-F]+$/.test(value) || value.length > 66) fail('invalid ' + label);
  return BigInt(value.toLowerCase());
}

function bigintValue(value: unknown, label: string): bigint {
  if (typeof value === 'bigint') return value;
  if (typeof value === 'string') return quantity(value, label);
  return fail('invalid ' + label);
}

function safeIndex(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) fail('invalid ' + label);
  return value;
}

function hash(value: unknown, label: string): Hex {
  return hex(value, label, 32);
}

function utf8Length(value: string): number {
  return textEncoder.encode(value).byteLength;
}

function canonical(value: unknown): string {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') {
    const serialized = JSON.stringify(value);
    if (serialized === undefined) fail('trace value cannot be serialized');
    return serialized;
  }
  if (typeof value === 'number') {
    if (!Number.isSafeInteger(value)) fail('trace contains non-safe number');
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
  const object = ownObject(value, 'trace value');
  return '{' + Object.keys(object).sort().map(key => JSON.stringify(key) + ':' + canonical(object[key])).join(',') + '}';
}

function boundRawTrace(value: unknown, label: string): void {
  const seen = new WeakSet<object>();
  let nodes = 0;
  let stringBytes = 0;
  function visit(item: unknown, depth: number): void {
    if (++nodes > MAX_TRACE_NODES || depth > MAX_TRACE_DEPTH) fail(label + ' node/depth bound exceeded');
    if (item === null || typeof item === 'boolean') return;
    if (typeof item === 'string') {
      stringBytes += utf8Length(item);
      if (stringBytes > MAX_TRACE_BYTES) fail(label + ' exceeds byte bound');
      return;
    }
    if (typeof item === 'number') {
      if (!Number.isSafeInteger(item)) fail(label + ' contains non-safe number');
      return;
    }
    if (!item || typeof item !== 'object') fail(label + ' contains unsupported value');
    if (seen.has(item)) fail(label + ' is cyclic');
    seen.add(item);
    if (Array.isArray(item)) {
      for (const child of item) visit(child, depth + 1);
      return;
    }
    const object = ownObject(item, label);
    for (const [key, child] of Object.entries(object)) {
      stringBytes += utf8Length(key);
      if (stringBytes > MAX_TRACE_BYTES) fail(label + ' exceeds byte bound');
      visit(child, depth + 1);
    }
  }
  visit(value, 0);
}

function traceCommitment(value: unknown, label: string): Hex {
  boundRawTrace(value, label);
  const serialized = canonical(value);
  if (utf8Length(serialized) > MAX_TRACE_BYTES) fail(label + ' exceeds byte bound');
  return keccak256(stringToHex(serialized));
}

function derivedBalanceSlot(holder: Address, mappingSlot: string): Hex {
  return keccak256(encodeAbiParameters(parseAbiParameters('address,uint256'),
    [holder, BigInt(mappingSlot)])).toLowerCase() as Hex;
}

function normalizedSlot(value: unknown, label: string): Hex {
  const raw = hex(value, label);
  if (raw.length > 66) fail('oversized ' + label);
  return ('0x' + raw.slice(2).padStart(64, '0')) as Hex;
}

function addressMap(raw: unknown, label: string): Map<string, TraceObject> {
  const value = ownObject(raw, label);
  const out = new Map<string, TraceObject>();
  const entries = Object.entries(value);
  if (entries.length > MAX_TRACE_ACCOUNTS) fail(label + ' account bound exceeded');
  for (const [key, account] of entries) {
    const normalized = address(key, label + ' account');
    const lower = normalized.toLowerCase();
    if (out.has(lower)) fail(label + ' has case-aliased account');
    out.set(lower, ownObject(account, label + ' account state'));
  }
  return out;
}

function storageMap(account: TraceObject, label: string, allowAbsent = false): Map<Hex, bigint> {
  // In prestateTracer diff mode, an omitted storage object is the empty set of
  // modified slots. The account itself must still be present; full prestate
  // remains mandatory and supplies the independently bound starting slot.
  if (!Object.hasOwn(account, 'storage')) {
    if (allowAbsent) return new Map<Hex, bigint>();
    fail('missing ' + label + ' storage');
  }
  const storage = ownObject(account.storage, label + ' storage');
  const result = new Map<Hex, bigint>();
  const entries = Object.entries(storage);
  if (entries.length > MAX_STORAGE_SLOTS) fail(label + ' storage bound exceeded');
  for (const [key, value] of entries) {
    const slot = normalizedSlot(key, label + ' storage slot');
    if (result.has(slot)) fail(label + ' has case-aliased storage slot');
    result.set(slot, quantity(value, label + ' storage value'));
  }
  return result;
}

type FullPrestateAccount = Readonly<{
  storage: Map<Hex, bigint>;
  runtimeHash: Hex;
}>;

function fullPrestateAccount(prestate: Map<string, TraceObject>, token: Address, label: string, runtimeHash: Hex): FullPrestateAccount {
  const account = prestate.get(token.toLowerCase());
  if (!account) fail('missing full prestate ' + label + ' account');
  const code = hex(account.code, 'full prestate ' + label + ' code');
  if (code === '0x' || keccak256(code).toLowerCase() !== runtimeHash) fail('full prestate ' + label + ' runtime mismatch');
  return { storage: storageMap(account, 'full prestate ' + label), runtimeHash };
}

function diffRuntime(account: TraceObject, label: string, runtimeHash: Hex): void {
  if (Object.hasOwn(account, 'code')) {
    const code = hex(account.code, 'diff ' + label + ' code');
    if (code === '0x' || keccak256(code).toLowerCase() !== runtimeHash) fail('diff ' + label + ' runtime mismatch');
  }
}

type TraceDiff = Readonly<{ pre: Map<string, TraceObject>; post: Map<string, TraceObject> }>;
type TraceBalance = Readonly<{
  before: bigint;
  after: bigint;
  delta: bigint;
  preSlotPresent: boolean;
  postSlotPresent: boolean;
}>;

function diffBalance(
  diff: TraceDiff,
  token: Address,
  slot: Hex,
  fullPre: FullPrestateAccount,
  label: string,
  allowUnchanged = false,
): TraceBalance {
  const preAccount = diff.pre.get(token.toLowerCase());
  const postAccount = diff.post.get(token.toLowerCase());
  if (!preAccount || !postAccount) fail('missing diff ' + label + ' token account');
  diffRuntime(preAccount, 'pre ' + label, fullPre.runtimeHash);
  diffRuntime(postAccount, 'post ' + label, fullPre.runtimeHash);
  const preSlots = storageMap(preAccount, 'diff pre ' + label, true);
  const postSlots = storageMap(postAccount, 'diff post ' + label, true);
  // Geth's diff mode explicitly represents insertion/deletion by omitting the
  // slot from pre/post respectively. Other missing fields are rejected above.
  const fromFull = fullPre.storage.get(slot);
  if (fromFull === undefined) fail('missing full prestate ' + label + ' balance slot');
  const neitherSlotPresent = !preSlots.has(slot) && !postSlots.has(slot);
  if (neitherSlotPresent && !allowUnchanged) fail('missing diff ' + label + ' balance slot');
  const before = preSlots.has(slot) ? preSlots.get(slot)! : neitherSlotPresent ? fromFull : 0n;
  const after = postSlots.has(slot) ? postSlots.get(slot)! : neitherSlotPresent ? fromFull : 0n;
  if (fromFull !== before) fail('full prestate/diff mismatch for ' + label);
  return {
    before,
    after,
    delta: after - before,
    preSlotPresent: preSlots.has(slot),
    postSlotPresent: postSlots.has(slot),
  };
}

function redactCallTree(raw: unknown, transaction: TraceTransaction): Readonly<{
  root: ExternalTerminalSettlementTraceCall;
  frames: number;
}> {
  let frames = 0;
  const entryPoint = address(transaction.to, 'entry point');
  const transactionFrom = address(transaction.from, 'transaction sender');
  const input = hex(transaction.input, 'transaction calldata');
  if ((input.length - 2) / 2 > MAX_CALLDATA_BYTES) fail('transaction calldata exceeds bound');
  function walk(frame: unknown, depth: number, root: boolean): ExternalTerminalSettlementTraceCall {
    if (++frames > MAX_TRACE_FRAMES || depth > MAX_TRACE_DEPTH) fail('call trace frame/depth bound exceeded');
    const current = ownObject(frame, 'call trace frame');
    if (typeof current.type !== 'string' || current.type.length === 0 || current.type.length > 32) fail('invalid call trace type');
    const from = address(current.from, 'call trace from');
    const to = address(current.to, 'call trace to');
    const callInput = hex(current.input, 'call trace input');
    const output = hex(current.output ?? '0x', 'call trace output');
    if ((callInput.length - 2) / 2 > MAX_CALLDATA_BYTES || (output.length - 2) / 2 > MAX_CALLDATA_BYTES) {
      fail('call trace payload exceeds bound');
    }
    if (root && (!isAddressEqual(to, entryPoint) || !isAddressEqual(from, transactionFrom)
      || callInput !== input || current.error !== undefined && current.error !== null)) fail('call trace root mismatch');
    if (current.calls !== undefined && !Array.isArray(current.calls)) fail('invalid call trace children');
    const calls = (current.calls ?? []).map(child => walk(child, depth + 1, false));
    return {
      type: current.type,
      from,
      to,
      gas: typeof current.gas === 'string' ? quantity(current.gas, 'call trace gas').toString() : null,
      gasUsed: typeof current.gasUsed === 'string' ? quantity(current.gasUsed, 'call trace gas used').toString() : null,
      inputSelector: callInput.length >= 10 ? callInput.slice(0, 10) as Hex : null,
      inputHash: keccak256(callInput),
      outputHash: keccak256(output),
      reverted: Boolean(current.error),
      calls,
    };
  }
  return { root: walk(raw, 0, true), frames };
}

type PackedUserOperation07 = Readonly<{
  sender: Address;
  nonce: bigint;
  initCode: Hex;
  callData: Hex;
  accountGasLimits: Hex;
  preVerificationGas: bigint;
  gasFees: Hex;
  paymasterAndData: Hex;
  signature: Hex;
}>;

function packedUserOperation(value: unknown): PackedUserOperation07 {
  const record = ownObject(value, 'packed operation');
  return {
    sender: address(record.sender, 'packed operation sender'),
    nonce: bigintValue(record.nonce, 'packed operation nonce'),
    initCode: hex(record.initCode, 'packed operation initCode'),
    callData: hex(record.callData, 'packed operation callData'),
    accountGasLimits: hex(record.accountGasLimits, 'packed operation accountGasLimits'),
    preVerificationGas: bigintValue(record.preVerificationGas, 'packed operation preVerificationGas'),
    gasFees: hex(record.gasFees, 'packed operation gasFees'),
    paymasterAndData: hex(record.paymasterAndData, 'packed operation paymasterAndData'),
    signature: hex(record.signature, 'packed operation signature'),
  };
}

function packedUint128Pair(value: Hex, label: string): readonly [bigint, bigint] {
  if (value.length !== 66) fail('invalid ' + label + ' length');
  return [BigInt('0x' + value.slice(2, 34)), BigInt('0x' + value.slice(34))];
}

function unpackUserOperation(packed: PackedUserOperation07): UserOperation<'0.7'> {
  const [verificationGasLimit, callGasLimit] = packedUint128Pair(packed.accountGasLimits, 'operation accountGasLimits');
  const [maxPriorityFeePerGas, maxFeePerGas] = packedUint128Pair(packed.gasFees, 'operation gasFees');
  const factory = packed.initCode === '0x' ? undefined : address(packed.initCode.slice(0, 42), 'operation factory');
  const factoryData = packed.initCode === '0x' || packed.initCode.length === 42
    ? undefined
    : hex('0x' + packed.initCode.slice(42), 'operation factory data');
  if (packed.paymasterAndData !== '0x' && packed.paymasterAndData.length < 106) fail('invalid operation paymasterAndData length');
  const paymaster = packed.paymasterAndData === '0x' ? undefined : address(packed.paymasterAndData.slice(0, 42), 'operation paymaster');
  const paymasterVerificationGasLimit = packed.paymasterAndData === '0x' ? undefined
    : BigInt('0x' + packed.paymasterAndData.slice(42, 74));
  const paymasterPostOpGasLimit = packed.paymasterAndData === '0x' ? undefined
    : BigInt('0x' + packed.paymasterAndData.slice(74, 106));
  const paymasterData = packed.paymasterAndData === '0x' || packed.paymasterAndData.length === 106 ? undefined
    : hex('0x' + packed.paymasterAndData.slice(106), 'operation paymaster data');
  return {
    sender: packed.sender,
    nonce: packed.nonce,
    callData: packed.callData,
    callGasLimit,
    verificationGasLimit,
    preVerificationGas: packed.preVerificationGas,
    maxFeePerGas,
    maxPriorityFeePerGas,
    ...(factory === undefined ? {} : { factory }),
    ...(factoryData === undefined ? {} : { factoryData }),
    ...(paymaster === undefined ? {} : { paymaster }),
    ...(paymasterData === undefined ? {} : { paymasterData }),
    ...(paymasterVerificationGasLimit === undefined ? {} : { paymasterVerificationGasLimit }),
    ...(paymasterPostOpGasLimit === undefined ? {} : { paymasterPostOpGasLimit }),
    signature: packed.signature,
  };
}

function receiptOperation(receipt: TraceReceipt, transaction: TraceTransaction, chainId: number, vault: Address): Readonly<{
  userOperationHash: Hex;
  nonce: string;
}> {
  if (receipt.status !== 'success') fail('transaction receipt is not successful');
  const decoded = decodeFunctionData({ abi: entryPoint07Abi, data: hex(transaction.input, 'transaction calldata') });
  if (decoded.functionName !== 'handleOps') fail('exactly one direct handleOps operation required');
  const operations = decoded.args[0];
  if (operations.length !== 1) fail('exactly one direct handleOps operation required');
  const operation = unpackUserOperation(packedUserOperation(operations[0]!));
  if (!isAddressEqual(address(operation.sender, 'selected operation sender'), vault)) fail('selected operation vault mismatch');
  const userOperationHash = getUserOperationHash({
    userOperation: operation,
    chainId,
    entryPointAddress: address(transaction.to, 'entry point'),
    entryPointVersion: '0.7',
  });
  const events: TraceObject[] = [];
  for (const log of receipt.logs ?? []) {
    if (!isAddressEqual(address(log.address, 'receipt log address'), address(transaction.to, 'entry point'))) continue;
    try {
      const event = decodeEventLog({
        abi: entryPoint07Abi,
        eventName: 'UserOperationEvent',
        topics: log.topics,
        data: log.data,
        strict: true,
      });
      // viem decodes other ABI events even when eventName is supplied against a
      // full ABI. Keep only the actual terminal event; BeforeExecution and
      // deposit/accounting events do not describe this UserOperation.
      if (event.eventName === 'UserOperationEvent') events.push(ownObject(event.args, 'terminal event arguments'));
    } catch {
      // Unrelated EntryPoint event.
    }
  }
  if (events.length !== 1) fail('single successful UserOperationEvent required');
  const event = events[0]!;
  if (!isAddressEqual(address(event.sender, 'terminal event sender'), vault)
    || bigintValue(event.nonce, 'terminal event nonce') !== BigInt(operation.nonce) || event.success !== true
    || hash(event.userOpHash, 'terminal event hash') !== userOperationHash.toLowerCase()) {
    fail('single successful UserOperationEvent required');
  }
  return { userOperationHash, nonce: BigInt(operation.nonce).toString() };
}

type SourceIdentity = Readonly<{
  transactionHash: Hex;
  blockHash: Hex;
  blockNumber: string;
  transactionIndex: number;
  entryPoint: Address;
  inputHash: Hex;
}>;

function sourceIdentity(
  transaction: TraceTransaction,
  receipt: TraceReceipt,
  block: TraceBlock,
  transactionHash: Hex,
): SourceIdentity {
  const txHash = hash(transaction.hash, 'transaction hash');
  const receiptHash = hash(receipt.transactionHash, 'receipt transaction hash');
  const expected = hash(transactionHash, 'requested transaction hash');
  if (txHash !== expected || receiptHash !== expected || transaction.blockHash === null || transaction.blockNumber === null
    || transaction.transactionIndex === null || receipt.blockHash === null || receipt.blockNumber === null || receipt.transactionIndex === null) {
    fail('incomplete transaction/receipt identity');
  }
  const blockHash = hash(transaction.blockHash, 'transaction block hash');
  const blockNumber = BigInt(transaction.blockNumber);
  const transactionIndex = safeIndex(transaction.transactionIndex, 'transaction index');
  if (hash(receipt.blockHash, 'receipt block hash') !== blockHash || bigintValue(receipt.blockNumber, 'receipt block number') !== blockNumber
    || safeIndex(receipt.transactionIndex, 'receipt transaction index') !== transactionIndex || bigintValue(block.number, 'block number') !== blockNumber
    || hash(block.hash, 'block hash') !== blockHash || !Array.isArray(block.transactions)
    || hash(block.transactions[transactionIndex], 'block transaction hash') !== expected) fail('transaction/receipt/block identity mismatch');
  const entryPoint = address(transaction.to, 'entry point');
  if (entryPoint.toLowerCase() !== ENTRY_POINT_V07 || entryPoint === ZERO_ADDRESS || receipt.to === null
    || !isAddressEqual(address(receipt.to, 'receipt entry point'), entryPoint)
    || transaction.value !== undefined && BigInt(transaction.value) !== 0n) fail('direct EntryPoint transaction required');
  return {
    transactionHash: expected,
    blockHash,
    blockNumber: blockNumber.toString(),
    transactionIndex,
    entryPoint,
    inputHash: keccak256(hex(transaction.input, 'transaction calldata')),
  };
}

async function localTrace(
  local: LocalTracePort,
  options: TraceObject,
  transactionHash: Hex,
): Promise<unknown> {
  const method = 'debug_traceTransaction';
  let result: unknown;
  try {
    result = await local(method, [transactionHash, options], 20_000);
  } catch (error) {
    const reason = error instanceof Error ? error.message.replace(/0x[0-9a-fA-F]{130,}/g, '[hex payload omitted]') : 'unknown error';
    fail(method + ' unavailable: ' + reason.slice(0, 300));
  }
  if (result === null || result === undefined) fail(method + ' returned no trace');
  return result;
}

async function localChainId(local: LocalTracePort): Promise<bigint> {
  return quantity(await local('eth_chainId', [], 20_000), 'local chain id');
}

async function snapshot(client: ExternalTerminalSettlementTraceClient, transactionHash: Hex,
  concurrentContext = false): Promise<TraceSnapshot> {
  // The native collector permits these independent reads to share one bounded
  // envelope. Each snapshot still reads both again; its block remains dependent
  // on the actual transaction. Legacy collectors retain their read ordering.
  const [transaction, receipt] = concurrentContext
    ? await Promise.all([client.getTransaction({ hash: transactionHash }), client.getTransactionReceipt({ hash: transactionHash })])
    : [await client.getTransaction({ hash: transactionHash }), await client.getTransactionReceipt({ hash: transactionHash })] as const;
  if (!transaction || !receipt || transaction.blockNumber === null) fail('transaction or receipt unavailable');
  const block = await client.getBlock({ blockNumber: transaction.blockNumber });
  if (!block || block.hash === null) fail('source block unavailable');
  return {
    transaction: structuredClone(transaction) as TraceSnapshot['transaction'],
    receipt: structuredClone(receipt),
    block: structuredClone(block) as TraceSnapshot['block'],
  };
}

/**
 * Reads genuine local-node transaction traces for the test-only ForkAsset model.
 * It has no DB, signing, submission, persistence, token-profile, or settlement
 * port. A successful result remains deliberately non-authorizing.
 */
export async function collectExternalTerminalSettlementTrace(
  input: ExternalTerminalSettlementTraceInput,
): Promise<ExternalTerminalSettlementTraceResult> {
  return collectFixedTraceForChain(input, 4663);
}

/** Selected terminal counterpart; historical fixed-profile callers stay4663. */
export async function collectSelectedExternalTerminalSettlementTrace(
  input: ExternalTerminalSettlementTraceInput,
): Promise<ExternalTerminalSettlementTraceResult> {
  return collectFixedTraceForChain(input, networkBuild.chainId);
}

async function collectFixedTraceForChain(
  input: ExternalTerminalSettlementTraceInput, expectedChainId: 4663 | 46630,
): Promise<ExternalTerminalSettlementTraceResult> {
  const captured = { ...input, expected: { ...input.expected } };
  const runtimeHash = hash(captured.tokenRuntimeHash, 'ForkAsset runtime hash');
  const profile = (token: string): DirectStorageTokenProfile => ({ schemaVersion: 1,
    kind: 'direct-erc20-storage-v1', token: address(token, 'token'), runtimeHash, templateHash: runtimeHash,
    decimals: 6, balanceMappingSlot: '0', familyId: 'controlled-forkasset-v1' });
  const core = await collectDirectStorageTrace(captured, profile(captured.tokenIn), profile(captured.tokenOut), expectedChainId);
  return Object.freeze({ ...core, scope: 'local-forkasset-transaction-trace-deltas-only',
    assets: { tokenIn: address(captured.tokenIn, 'token in'), tokenOut: address(captured.tokenOut, 'token out'),
      vault: address(captured.vault, 'vault'), treasury: address(captured.treasury, 'treasury'),
      runtimeHash, decimals: 6 as const, balanceMappingSlot: '0' as const },
    limitations: [
      'ForkAsset balanceOf mapping slot 0 only; no arbitrary-token semantics.',
      'No SMA-B validation/module or execution-time owner proof.',
      'Local trace availability and deltas only; no persistence, settlement, fee, P&L, referral, points, signing, or submission authority.',
    ] });
}

function readDirectStorageProfile(value: DirectStorageTokenProfile): DirectStorageTokenProfile {
  const p = ownObject(structuredClone(value), 'direct-storage profile');
  const keys = ['schemaVersion', 'kind', 'token', 'runtimeHash', 'templateHash', 'decimals', 'balanceMappingSlot', 'familyId'];
  if (Object.keys(p).length !== keys.length || keys.some(key => !Object.hasOwn(p, key))
    || p.schemaVersion !== 1 || p.kind !== 'direct-erc20-storage-v1'
    || typeof p.decimals !== 'number' || !Number.isInteger(p.decimals) || p.decimals < 0 || p.decimals > 18
    || typeof p.balanceMappingSlot !== 'string' || !/^(?:0|[1-9][0-9]{0,77})$/.test(p.balanceMappingSlot)
    || BigInt(p.balanceMappingSlot) >= 2n ** 256n
    || typeof p.familyId !== 'string' || !/^[A-Za-z0-9:._-]{1,128}$/.test(p.familyId)) fail('invalid direct-storage profile');
  const token = address(p.token, 'profile token');
  if (token.toLowerCase() === ZERO_ADDRESS) fail('zero profile token');
  return Object.freeze({ schemaVersion: 1, kind: 'direct-erc20-storage-v1', token,
    runtimeHash: hash(p.runtimeHash, 'profile runtime'), templateHash: hash(p.templateHash, 'profile template'),
    decimals: p.decimals, balanceMappingSlot: p.balanceMappingSlot, familyId: p.familyId });
}

/** Observes independently configured immutable storage layouts. The profile
 * itself does not prove its semantic claims or authorize financial publication. */
export async function collectProfiledTerminalSettlementTrace(
  input: ProfiledTerminalSettlementTraceInput,
): Promise<ProfiledTerminalSettlementTraceResult> {
  return collectProfiledTraceForChain(input, 4663);
}

/** Active terminal domain is immutable build configuration, not caller input. */
export async function collectSelectedProfiledTerminalSettlementTrace(
  input: ProfiledTerminalSettlementTraceInput,
): Promise<ProfiledTerminalSettlementTraceResult> {
  return collectProfiledTraceForChain(input, networkBuild.chainId);
}

async function collectProfiledTraceForChain(
  input: ProfiledTerminalSettlementTraceInput, expectedChainId: 4663 | 46630,
): Promise<ProfiledTerminalSettlementTraceResult> {
  const tokenInProfile = readDirectStorageProfile(input.tokenInProfile);
  const tokenOutProfile = readDirectStorageProfile(input.tokenOutProfile);
  const captured = { local: input.local, client: input.client, transactionHash: input.transactionHash,
    vault: input.vault, treasury: input.treasury, expected: { ...input.expected },
    tokenIn: tokenInProfile.token, tokenOut: tokenOutProfile.token };
  const core = await collectDirectStorageTrace(captured, tokenInProfile, tokenOutProfile, expectedChainId);
  return Object.freeze({ ...core, scope: 'direct-storage-profile-transaction-trace-deltas-only',
    assets: Object.freeze({ vault: address(captured.vault, 'vault'), treasury: address(captured.treasury, 'treasury'),
      tokenInProfile, tokenOutProfile }),
    limitations: [
      'Only independently reviewed direct-storage layouts; no inferred proxy, rebasing or arbitrary-token semantics.',
      'No SMA-B validation/module or execution-time owner proof.',
      'Provider observations only; no token admission, persistence, accounting, signing or submission authority.',
    ] });
}

async function collectDirectStorageTrace(
  input: Omit<ExternalTerminalSettlementTraceInput, 'tokenRuntimeHash'>,
  tokenInProfile: DirectStorageTokenProfile,
  tokenOutProfile: DirectStorageTokenProfile,
  expectedChainId: 4663 | 46630,
): Promise<Omit<ExternalTerminalSettlementTraceResult, 'scope' | 'assets' | 'limitations'>> {
  const { local, client, transactionHash, vault, tokenIn, tokenOut, treasury, expected } = input;
  if (typeof local !== 'function') fail('local RPC function required');
  if (!client || typeof client !== 'object') fail('public client required');
  const requestedHash = hash(transactionHash, 'transaction hash');
  const addresses = {
    vault: address(vault, 'vault'),
    tokenIn: address(tokenIn, 'token in'),
    tokenOut: address(tokenOut, 'token out'),
    treasury: address(treasury, 'treasury'),
  };
  if (isAddressEqual(addresses.tokenIn, addresses.tokenOut) || isAddressEqual(addresses.vault, addresses.treasury)
    || [addresses.vault, addresses.treasury].some(holder => isAddressEqual(holder, addresses.tokenIn)
      || isAddressEqual(holder, addresses.tokenOut))) {
    fail('distinct vault, assets, and treasury required');
  }
  const amounts = ownObject(expected, 'expected amounts');
  const amountIn = typeof amounts.amountIn === 'bigint' ? amounts.amountIn : fail('expected amountIn must be bigint');
  const netOutput = typeof amounts.netOutput === 'bigint' ? amounts.netOutput : fail('expected netOutput must be bigint');
  const fee = typeof amounts.fee === 'bigint' ? amounts.fee : fail('expected fee must be bigint');
  if (amountIn <= 0n || netOutput <= 0n || fee < 0n) fail('invalid expected amounts');

  const before = await snapshot(client, requestedHash);
  const chainId = await client.getChainId();
  if (chainId !== expectedChainId || await localChainId(local) !== BigInt(expectedChainId)) fail('local/client chain mismatch');
  const identity = sourceIdentity(before.transaction, before.receipt, before.block, requestedHash);
  const operation = receiptOperation(before.receipt, before.transaction, chainId, addresses.vault);

  const prestateOptions = { tracer: 'prestateTracer', timeout: '5s' } as const;
  const diffOptions = { tracer: 'prestateTracer', tracerConfig: { diffMode: true }, timeout: '5s' } as const;
  const callOptions = { tracer: 'callTracer', tracerConfig: { withLog: true }, timeout: '5s' } as const;
  const fullPrestateRaw = await localTrace(local, prestateOptions, requestedHash);
  const diffRaw = await localTrace(local, diffOptions, requestedHash);
  const callRaw = await localTrace(local, callOptions, requestedHash);
  const commitments = {
    fullPrestate: traceCommitment(fullPrestateRaw, 'full prestate trace'),
    diff: traceCommitment(diffRaw, 'diff trace'),
    call: traceCommitment(callRaw, 'call trace'),
  };

  const fullPrestate = addressMap(fullPrestateRaw, 'full prestate');
  const diffObject = ownObject(diffRaw, 'diff trace');
  if (!Object.hasOwn(diffObject, 'pre') || !Object.hasOwn(diffObject, 'post')) fail('diff trace pre/post required');
  const diff: TraceDiff = {
    pre: addressMap(diffObject.pre, 'diff pre'),
    post: addressMap(diffObject.post, 'diff post'),
  };
  const inputPre = fullPrestateAccount(fullPrestate, addresses.tokenIn, 'token-in', tokenInProfile.runtimeHash);
  const outputPre = fullPrestateAccount(fullPrestate, addresses.tokenOut, 'token-out', tokenOutProfile.runtimeHash);
  const [inputCode, outputCode] = await Promise.all([
    client.getBytecode({ address: addresses.tokenIn, blockNumber: before.transaction.blockNumber }),
    client.getBytecode({ address: addresses.tokenOut, blockNumber: before.transaction.blockNumber }),
  ]);
  if (!inputCode || !outputCode || inputCode === '0x' || outputCode === '0x'
    || keccak256(inputCode).toLowerCase() !== tokenInProfile.runtimeHash
    || keccak256(outputCode).toLowerCase() !== tokenOutProfile.runtimeHash) {
    fail('post-trace ForkAsset runtime mismatch');
  }

  const inputVault = diffBalance(diff, addresses.tokenIn, derivedBalanceSlot(addresses.vault, tokenInProfile.balanceMappingSlot), inputPre, 'token-in vault');
  const outputVault = diffBalance(diff, addresses.tokenOut, derivedBalanceSlot(addresses.vault, tokenOutProfile.balanceMappingSlot), outputPre, 'token-out vault');
  const outputTreasury = diffBalance(
    diff,
    addresses.tokenOut,
    derivedBalanceSlot(addresses.treasury, tokenOutProfile.balanceMappingSlot),
    outputPre,
    'token-out treasury',
    fee === 0n,
  );
  if (inputVault.delta !== -amountIn || outputVault.delta !== netOutput || outputTreasury.delta !== fee) {
    fail('trace balance deltas do not match expected settlement amounts');
  }
  const call = redactCallTree(callRaw, before.transaction);

  const after = await snapshot(client, requestedHash);
  const rechecked = sourceIdentity(after.transaction, after.receipt, after.block, requestedHash);
  if (canonical(identity) !== canonical(rechecked)
    || receiptOperation(after.receipt, after.transaction, chainId, addresses.vault).userOperationHash !== operation.userOperationHash
    || await client.getChainId() !== expectedChainId || await localChainId(local) !== BigInt(expectedChainId)) fail('source changed during trace collection');

  const balance = (value: TraceBalance): ExternalTerminalSettlementTraceBalance => ({
    before: value.before.toString(),
    after: value.after.toString(),
    delta: value.delta.toString(),
    preSlotPresent: value.preSlotPresent,
    postSlotPresent: value.postSlotPresent,
  });
  const result: Omit<ExternalTerminalSettlementTraceResult, 'scope' | 'assets' | 'limitations'> = {
    schemaVersion: 1,
    chainId,
    source: {
      ...identity,
      userOperationHash: operation.userOperationHash,
      nonce: operation.nonce,
      traceCommitments: commitments,
      traceProfile: {
        prestate: 'prestateTracer',
        diff: 'prestateTracer.diffMode',
        call: 'callTracer.withLog',
      },
      callTrace: call.root,
      traceNotCryptographicProof: true,
    },
    deltas: {
      tokenInVault: balance(inputVault),
      tokenOutVault: balance(outputVault),
      tokenOutTreasury: balance(outputTreasury),
    },
    traceBounds: {
      maxBytesPerTrace: MAX_TRACE_BYTES,
      maxAccounts: MAX_TRACE_ACCOUNTS,
      maxStorageSlotsPerAccount: MAX_STORAGE_SLOTS,
      maxFrames: MAX_TRACE_FRAMES,
      maxDepth: MAX_TRACE_DEPTH,
      observedFrames: call.frames,
    },
    ...flags,
  };
  return Object.freeze(result);
}

export type NativeTerminalSettlementTraceInput = Readonly<{
  local: LocalTracePort;
  client: ExternalTerminalSettlementTraceClient;
  /** Must be the bounded historical EIP-1898 client, not latest/number fallback. */
  stateClient: NativeWrapperObservationClient;
  transactionHash: Hex;
  owner: SmaBDeployedUserOpValidationTraceInput['expected'];
  nativeTrade: Parameters<typeof decodeAndValidateNativeV2Trade>[0];
  wrapperProfile: NativeWrapperObservationProfile;
  endpointProfile: DirectStorageTokenProfile;
  pool?: Readonly<{ address: Address; codeHash: Hex }>;
}>;

const nativeImplSlot = '0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc' as Hex;
const nativeAdminSlot = '0xb53127684a568b3173ae13b9f8a6016e243e63b6e8ee1178d6a717850b5d6103' as Hex;
const nativeTransferAbi = parseAbi(['event Transfer(address indexed from,address indexed to,uint256 value)']);
const directWrapperEventsAbi = parseAbi(['event Deposit(address indexed from,uint256 amount)',
  'event Withdrawal(address indexed to,uint256 amount)']);
const nativeSwapAbi = parseAbi(['event Swap(address indexed sender,uint256 amount0In,uint256 amount1In,uint256 amount0Out,uint256 amount1Out,address indexed to)']);
const nativeWrapperCallsAbi = parseAbi(['function deposit() payable', 'function withdraw(uint256 amount)',
  'function transfer(address to,uint256 amount) returns(bool)', 'function balanceOf(address holder) view returns(uint256)']);
const nativePoolCallsAbi = parseAbi(['function swap(uint256 amount0Out,uint256 amount1Out,address to,bytes data)']);

/** Actual transaction cashflows for distinct reviewed aeWETH and direct-WETH layouts.
 * Its ERC20 Transfer mint/burn events are NOT ETH deposit/withdraw evidence on
 * their own. Successful deployed/first-vault observation requires owner
 * validation, CALL values, source-specific delegate/immutable checks, storage
 * and source fences. Failed
 * execution publishes no trade effects and retains separately reconciled gas.
 * All outputs are inert. */
export async function collectNativeTerminalSettlementTrace(input: NativeTerminalSettlementTraceInput) {
  return collectNativeTraceForChain(input, 4663);
}

/** Selected terminal counterpart; historical4663 collection is unchanged. */
export async function collectSelectedNativeTerminalSettlementTrace(input: NativeTerminalSettlementTraceInput) {
  return collectNativeTraceForChain(input, networkBuild.chainId);
}

async function collectNativeTraceForChain(input: NativeTerminalSettlementTraceInput, expectedChainId: 4663 | 46630) {
  const runtime = input.client, stateRuntime = input.stateClient, actualLocal = input.local;
  const client: ExternalTerminalSettlementTraceClient = { getChainId: runtime.getChainId.bind(runtime),
    getTransaction: runtime.getTransaction.bind(runtime), getTransactionReceipt: runtime.getTransactionReceipt.bind(runtime),
    getBlock: runtime.getBlock.bind(runtime), getBytecode: runtime.getBytecode.bind(runtime) };
  const state: NativeWrapperObservationClient = { getChainId: stateRuntime.getChainId.bind(stateRuntime),
    getBlock: stateRuntime.getBlock.bind(stateRuntime), getBytecode: stateRuntime.getBytecode.bind(stateRuntime),
    getStorageAt: stateRuntime.getStorageAt.bind(stateRuntime), getBalance: stateRuntime.getBalance.bind(stateRuntime),
    readContract: stateRuntime.readContract.bind(stateRuntime) };
  const saved = structuredClone({ transactionHash: input.transactionHash, owner: input.owner, nativeTrade: input.nativeTrade,
    wrapperProfile: input.wrapperProfile, endpointProfile: input.endpointProfile, pool: input.pool });
  const [{ decodeAndValidateNativeV2Trade }, { nativeWrapperObservationAbi, readNativeWrapperObservationProfile, readLegacyNativeWrapperObservationProfile,
    nativeWrapperRouterWeth9Offsets },
    { collectNativeSmaBUserOpValidationTrace, collectSelectedNativeSmaBUserOpValidationTrace },
    { extractOwnerOperationExecutionLogs, extractSelectedOwnerOperationExecutionLogs }] = await Promise.all([
    import('./nativeRouterTradeDecoder.js'), import('./nativeWrapperObservation.js'),
    import('./smaBFirstUserOpValidationTrace.js'), import('./operationExecutionLogs.js')]);
  const profile = expectedChainId === 4663 ? readLegacyNativeWrapperObservationProfile(saved.wrapperProfile)
    : readNativeWrapperObservationProfile(saved.wrapperProfile);
  const direct = profile.profile === 'reviewed-solmate-direct-weth-v1';
  const legacyAuthority = direct ? {} : { upgradeAuthorityVerified: false as const };
  const routerWeth9Offsets = nativeWrapperRouterWeth9Offsets(profile, saved.nativeTrade.policy.requiredDecoderVersion);
  const endpoint = readDirectStorageProfile(saved.endpointProfile);
  const txHash = hash(saved.transactionHash, 'native transaction hash');
  const before = await snapshot(client, txHash, true);
  const identity = sourceIdentity(before.transaction, before.receipt, before.block, txHash);
  const timestamp = bigintValue(before.block.timestamp, 'native source timestamp');
  const signedHandle = decodeFunctionData({ abi: entryPoint07Abi, data: before.transaction.input });
  if (signedHandle.functionName !== 'handleOps' || signedHandle.args[0].length !== 1) fail('exactly one native operation required');
  const extractLogs = expectedChainId === 4663 ? extractOwnerOperationExecutionLogs : extractSelectedOwnerOperationExecutionLogs;
  const segment = extractLogs({ receipt: before.receipt, transaction: before.transaction,
    expectedHash: saved.owner.userOperationHash, sender: saved.owner.vault, nonce: signedHandle.args[0][0]!.nonce });
  const routerTerms = decodeFunctionData({ abi: parseAbi(['function execute(bytes,bytes[],uint256) payable']), data: saved.nativeTrade.calls.at(-1)!.data });
  const originalDeadline = routerTerms.args[2];
  // Failed inclusion may be caused by expiry. Validate the ORIGINAL signed
  // terms before their deadline; this is structural restoration only, never a
  // claim that the execution occurred then or a renewed state/read clock.
  const termsTimestamp = segment.success ? timestamp : [timestamp, originalDeadline - 1n,
    BigInt(Math.floor(Date.parse(saved.nativeTrade.intent.expiresAt) / 1000)) - 1n,
    BigInt(Math.floor(Date.parse(saved.nativeTrade.quote.expiresAt) / 1000)) - 1n].reduce((a, b) => a < b ? a : b);
  const nativeTrade = { ...saved.nativeTrade, policy: { ...saved.nativeTrade.policy, nowUnixSeconds: termsTimestamp } };
  const decoded = decodeAndValidateNativeV2Trade(nativeTrade);
  const swap = decoded.commands.find(command => command.kind === 'SWAP');
  if (!swap || swap.kind !== 'SWAP') fail('native swap required');
  const vault = address(saved.owner.vault, 'native vault'), router = profile.router.account, wrapper = profile.wrapper.account;
  const treasury = address(nativeTrade.policy.feeRecipient, 'native treasury'), pool = saved.pool ? address(saved.pool.address, 'native pool') : null;
  const buy = decoded.nativeDirection === 'INPUT';
  if (!isAddressEqual(router, address(nativeTrade.policy.router, 'native router')) || !isAddressEqual(vault, swap.swapper)
    || !isAddressEqual(wrapper, nativeTrade.nativeWrapper)
    || !isAddressEqual(endpoint.token, decoded.route.tokens[buy ? 1 : 0]!)
    || new Set([vault, router, wrapper, endpoint.token, ...(pool ? [pool] : [])].map(value => value.toLowerCase())).size !== (pool ? 5 : 4)
    || [router, wrapper, endpoint.token, ...(pool ? [pool] : [])].some(value => isAddressEqual(value, treasury))) fail('native source role mismatch');
  // Only three bounded debug traces are acquired in this observation. The
  // owner/cashflow consumers share these exact raw responses, not caller proof.
  const cache = new Map<string, unknown>();
  const local: LocalTracePort = async (method, params, timeout) => {
    if (method !== 'debug_traceTransaction') return actualLocal(method, params, timeout);
    const key = canonical(params);
    if (!cache.has(key)) { if (cache.size >= 3) fail('native trace request bound'); cache.set(key, structuredClone(await actualLocal(method, params, timeout))); }
    return structuredClone(cache.get(key));
  };
  const collectOwner = expectedChainId === 4663 ? collectNativeSmaBUserOpValidationTrace : collectSelectedNativeSmaBUserOpValidationTrace;
  const owner = await collectOwner({ client, local, transactionHash: txHash,
    expected: saved.owner, nativeTrade });
  for (const key of ['transactionHash', 'blockHash', 'blockNumber', 'transactionIndex', 'inputHash'] as const) {
    if (owner.evidence.source[key] !== identity[key]) fail('native owner source mismatch');
  }
  const preRaw = await localTrace(local, { tracer: 'prestateTracer', timeout: '5s' }, txHash);
  const diffRaw = await localTrace(local, { tracer: 'prestateTracer', tracerConfig: { diffMode: true }, timeout: '5s' }, txHash);
  const callRaw = await localTrace(local, { tracer: 'callTracer', tracerConfig: { withLog: true }, timeout: '5s' }, txHash);
  const commitments = { fullPrestate: traceCommitment(preRaw, 'native prestate'), diff: traceCommitment(diffRaw, 'native diff'),
    call: traceCommitment(callRaw, 'native call') };
  const full = addressMap(preRaw, 'native prestate'), rawDiff = ownObject(diffRaw, 'native diff');
  const diff: TraceDiff = { pre: addressMap(rawDiff.pre, 'native diff pre'), post: addressMap(rawDiff.post, 'native diff post') };
  if (segment.success !== owner.success || segment.nonce !== BigInt(owner.evidence.source.nonce)) fail('native owner/receipt outcome mismatch');
  function nativeBalance(account: Address, label: string): TraceBalance {
    const prestate = full.get(account.toLowerCase());
    if (!prestate || !Object.hasOwn(prestate, 'balance')) fail('missing full prestate native ' + label + ' balance');
    const original = quantity(prestate.balance, label + ' balance'), pre = diff.pre.get(account.toLowerCase()), post = diff.post.get(account.toLowerCase());
    if (Boolean(pre) !== Boolean(post)
      && !(owner.accountDeployment === 'created-in-selected-transaction' && isAddressEqual(account, vault) && post)) fail('native account creation/deletion unsupported');
    const from = pre && Object.hasOwn(pre, 'balance') ? quantity(pre.balance, label + ' diff pre balance') : original;
    const to = post && Object.hasOwn(post, 'balance') ? quantity(post.balance, label + ' diff post balance') : original;
    if (from !== original) fail('native prestate/diff balance mismatch');
    return { before: from, after: to, delta: to - from, preSlotPresent: !!pre, postSlotPresent: !!post };
  }
  const nativeVault = nativeBalance(vault, 'vault');
  const entryPointNative = nativeBalance(address(identity.entryPoint, 'entry point'), 'entry point');
  // Pinned EntryPoint v0.7 StakeManager: deposits is mapping slot 0 and
  // DepositInfo.deposit is the first full uint256 word. The owner observer
  // checks that exact runtime before this layout is interpreted. A missing
  // accessed prestate slot never becomes a fabricated zero deposit.
  const entryPointRuntimeHash = keccak256(hex(full.get(identity.entryPoint.toLowerCase())!.code, 'EntryPoint runtime'));
  const depositStorageSlot = derivedBalanceSlot(vault, '0');
  const deposit = diffBalance(diff, identity.entryPoint, depositStorageSlot,
    fullPrestateAccount(full, identity.entryPoint, 'EntryPoint', entryPointRuntimeHash), 'EntryPoint deposit', true);
  const packedGas = signedHandle.args[0][0]!;
  const accountGas = BigInt(packedGas.accountGasLimits), gasFees = BigInt(packedGas.gasFees), uint128Mask = (1n << 128n) - 1n;
  const requiredPrefund = ((accountGas >> 128n) + (accountGas & uint128Mask) + packedGas.preVerificationGas) * (gasFees & uint128Mask);
  const missingFunds = requiredPrefund > deposit.before ? requiredPrefund - deposit.before : 0n;
  if (owner.prefundPaid !== missingFunds || segment.actualGasCost > requiredPrefund) fail('EntryPoint required prefund/deposit mismatch');
  if (deposit.delta !== owner.prefundPaid - segment.actualGasCost
    || entryPointNative.delta !== deposit.delta) fail('EntryPoint deposit/prefund/gas conservation mismatch');
  function reconcileGas(nativePrincipalDelta: bigint, vaultNativeTreasuryDelta = 0n) {
    const vaultNativeGasDelta = nativeVault.delta - nativePrincipalDelta - vaultNativeTreasuryDelta;
    const vaultCombinedGasDelta = vaultNativeGasDelta + deposit.delta;
    if (vaultNativeGasDelta !== -owner.prefundPaid || vaultCombinedGasDelta !== -segment.actualGasCost) fail('native principal/gas conservation mismatch');
    return { scope: 'selected-transaction-entrypoint-v0.7-deposit' as const,
      entryPoint: identity.entryPoint, entryPointRuntimeHash, depositMappingSlot: '0' as const, depositStorageSlot,
      depositBefore: deposit.before, depositAfter: deposit.after, depositDelta: deposit.delta,
      prefundPaid: owner.prefundPaid, actualGasCost: segment.actualGasCost, nativePrincipalDelta, vaultNativeTreasuryDelta,
      vaultNativeDelta: nativeVault.delta, vaultNativeGasDelta, vaultCombinedGasDelta };
  }
  const source = { number: BigInt(identity.blockNumber), hash: identity.blockHash, timestamp };
  async function sourceFence() {
    const block = await state.getBlock({ blockNumber: source.number });
    if (block.number !== source.number || block.hash?.toLowerCase() !== source.hash || block.timestamp !== timestamp
      || await state.getChainId() !== expectedChainId) fail('native historical source changed');
  }
  async function finalSource() {
    const after = await snapshot(client, txHash, true);
    const fingerprint = (value: unknown) => JSON.stringify(value, (_key, item: unknown) => typeof item === 'bigint' ? item.toString() : item);
    if (canonical(identity) !== canonical(sourceIdentity(after.transaction, after.receipt, after.block, txHash))
      || fingerprint(before.receipt.logs) !== fingerprint(after.receipt.logs) || after.receipt.status !== before.receipt.status
      || after.transaction.from !== before.transaction.from || after.transaction.to !== before.transaction.to
      || after.transaction.value !== before.transaction.value || after.transaction.chainId !== before.transaction.chainId
      || after.transaction.type !== before.transaction.type
      || await client.getChainId() !== expectedChainId || await localChainId(local) !== BigInt(expectedChainId)) fail('native source changed during collection');
    await sourceFence();
  }
  const accountDeployment = owner.accountDeployment;
  const scope = accountDeployment === 'already-deployed' ? 'historical-native-v2-deployed-vault-cashflows' as const
    : 'historical-native-v2-first-vault-cashflows' as const;
  if (!segment.success) {
    const gasReconciliation = reconcileGas(0n), values: Array<{ from: Address; to: Address; value: bigint }> = [];
    const redacted = redactCallTree(callRaw, before.transaction);
    function committedValues(raw: unknown, reverted = false) {
      const frame = ownObject(raw, 'reverted native frame');
      if (!['CALL', 'STATICCALL', 'DELEGATECALL', ...(accountDeployment === 'created-in-selected-transaction' ? ['CREATE2'] : [])].includes(String(frame.type))) fail('unsupported reverted native trace effect type');
      const failed = reverted || frame.error !== undefined && frame.error !== null;
      const value = quantity(frame.value ?? '0x0', 'reverted native value');
      if (!failed && value !== 0n && frame.type !== 'DELEGATECALL') {
        if (frame.type !== 'CALL') fail('unexpected committed native effect');
        values.push({ from: address(frame.from, 'committed from'), to: address(frame.to, 'committed to'), value });
      }
      for (const child of frame.calls as unknown[] | undefined ?? []) committedValues(child, failed);
    }
    committedValues(callRaw);
    const handle = decodeFunctionData({ abi: entryPoint07Abi, data: before.transaction.input });
    if (handle.functionName !== 'handleOps') fail('handleOps required');
    const expectedValues = [
      ...(owner.prefundPaid === 0n ? [] : [{ from: vault, to: identity.entryPoint, value: owner.prefundPaid }]),
      ...(segment.actualGasCost === 0n ? [] : [{ from: identity.entryPoint, to: handle.args[1], value: segment.actualGasCost }]),
    ];
    if (values.length !== expectedValues.length || values.some((value, index) => value.value !== expectedValues[index]!.value
      || !isAddressEqual(value.from, expectedValues[index]!.from) || !isAddressEqual(value.to, expectedValues[index]!.to))) fail('unexpected committed reverted-operation cashflow');
    // A reverted subtree cannot commit token/proxy/pool effects. Require the
    // transaction diff to agree; do not interpret attempted trace logs as logs.
    for (const target of [wrapper, ...(profile.profile === 'reviewed-solmate-direct-weth-v1' ? [] : [profile.implementation.account]),
      endpoint.token, router, ...(pool ? [pool] : []), treasury]) {
      if (isAddressEqual(target, vault) || isAddressEqual(target, before.transaction.from)) continue;
      if (diff.pre.has(target.toLowerCase()) || diff.post.has(target.toLowerCase())) fail('reverted trade retained external state change');
    }
    if (segment.candidateLogs.some(log => [wrapper, endpoint.token, ...(pool ? [pool] : [])].some(target => isAddressEqual(log.address, target)))) fail('reverted trade retained execution logs');
    await finalSource();
    return Object.freeze({ schemaVersion: 1 as const, scope, accountDeployment,
      source: { ...identity, timestamp, traceCommitments: commitments, traceNotCryptographicProof: true as const },
      owner, profile, endpointProfile: endpoint, outcome: 'reverted' as const, reason: null, trade: null,
      nativePrincipalDelta: 0n, gasReconciliation, verifiedEntryPointPrefundPaid: owner.prefundPaid,
      userOperationActualGasCost: segment.actualGasCost, entryPointDepositReconciliationVerified: true as const, vaultGasDebitConserved: true as const,
      balances: { nativeVault, entryPointNative, entryPointDeposit: deposit },
      traceBounds: { maxBytesPerTrace: MAX_TRACE_BYTES, maxFrames: MAX_TRACE_FRAMES, observedFrames: redacted.frames, actualDebugRequests: cache.size },
      ...legacyAuthority, submissionAuthorized: false as const, ...flags });
  }
  if (!pool || !saved.pool) fail('successful native route requires observed pool');
  const wrapperPre = fullPrestateAccount(full, wrapper, direct ? 'direct wrapper' : 'wrapper proxy', profile.wrapper.codeHash);
  const tokenPre = fullPrestateAccount(full, endpoint.token, 'native endpoint', endpoint.runtimeHash);
  const routerPre = full.get(router.toLowerCase());
  if (!routerPre || keccak256(hex(routerPre.code, 'native router prestate code')) !== profile.router.codeHash) fail('native router prestate runtime mismatch');
  const poolPre = full.get(pool.toLowerCase());
  if (!poolPre || keccak256(hex(poolPre.code, 'native pool prestate code')) !== hash(saved.pool.codeHash, 'pool runtime')) fail('native pool prestate runtime mismatch');
  if (profile.profile !== 'reviewed-solmate-direct-weth-v1') {
    const implPre = full.get(profile.implementation.account.toLowerCase());
    if (!implPre || keccak256(hex(implPre.code, 'wrapper implementation code')) !== profile.implementation.codeHash) fail('wrapper implementation prestate mismatch');
  }
  await sourceFence();
  const executionCodes = new Map<string, Hex>();
  for (const pin of [profile.router, profile.wrapper,
    ...(profile.profile === 'reviewed-solmate-direct-weth-v1' ? [] : [profile.implementation]),
    { account: endpoint.token, codeHash: endpoint.runtimeHash }, { account: pool, codeHash: hash(saved.pool.codeHash, 'pool runtime') }]) {
    const account = full.get(pin.account.toLowerCase());
    if (!account) fail('required native execution runtime prestate missing');
    const code = hex(account.code, 'native execution code');
    if (code === '0x' || keccak256(code).toLowerCase() !== pin.codeHash.toLowerCase()) fail('native execution runtime mismatch');
    executionCodes.set(pin.account.toLowerCase(), code);
    const pre = diff.pre.get(pin.account.toLowerCase()), post = diff.post.get(pin.account.toLowerCase());
    if (Boolean(pre) !== Boolean(post) || pre && Object.hasOwn(pre, 'code') && hex(pre.code, 'runtime pre') !== code
      || post && Object.hasOwn(post, 'code')) fail('in-transaction native runtime change');
  }
  const routerCode = executionCodes.get(router.toLowerCase())!;
  for (const offset of routerWeth9Offsets) {
    if (routerCode.slice(2 + (offset - 1) * 2, 2 + offset * 2) !== '7f'
      || routerCode.slice(2 + offset * 2, 2 + (offset + 32) * 2).toLowerCase() !== wrapper.slice(2).padStart(64, '0')) fail('native router WETH9 association mismatch');
  }
  type NativeFrame = { from: Address; to: Address; context: Address; type: string; input: Hex; output: Hex; value: bigint; valuePresent: boolean; parent: NativeFrame | null; calls: NativeFrame[]; logs: TraceObject[] };
  const nodes: NativeFrame[] = [];
  function walk(raw: unknown, parent: NativeFrame | null): NativeFrame {
    const value = ownObject(raw, 'native frame');
    if (value.error !== undefined && value.error !== null) fail('reverted native descendant unsupported');
    const target = address(value.to, 'native to'), type = String(value.type);
    if (!['CALL', 'STATICCALL', 'DELEGATECALL'].includes(type)
      && !(type === 'CREATE2' && accountDeployment === 'created-in-selected-transaction'
        && isAddressEqual(target, vault) && isAddressEqual(address(value.from, 'create from'), saved.owner.factory)
        && quantity(value.value ?? '0x0', 'create value') === 0n)) fail('unsupported native trace effect type');
    const frame: NativeFrame = { from: address(value.from, 'native from'), to: target, context: type === 'DELEGATECALL' ? parent?.context ?? fail('delegate parent required') : target, type,
      input: hex(value.input, 'native input'), output: hex(value.output ?? '0x', 'native output'),
      value: quantity(value.value ?? '0x0', 'native value'), valuePresent: value.value !== undefined, parent, calls: [], logs: [] };
    nodes.push(frame);
    if (value.calls !== undefined && !Array.isArray(value.calls) || value.logs !== undefined && !Array.isArray(value.logs)) fail('native frame children/logs invalid');
    frame.logs = (value.logs as unknown[] | undefined ?? []).map(log => ownObject(log, 'native frame log'));
    if (frame.logs.some(log => !isAddressEqual(address(log.address, 'native emitter'), frame.context))) fail('native trace log storage context mismatch');
    frame.calls = (value.calls as unknown[] | undefined ?? []).map(child => walk(child, frame)); return frame;
  }
  const redacted = redactCallTree(callRaw, before.transaction); walk(callRaw, null);
  // Admin code is not exercised in the supported selected transaction. Its
  // unaccessed runtime may only be a separate block-end observation. These
  // execution guards also apply before any contamination/unavailable return.
  if (profile.profile === 'reviewed-solmate-direct-weth-v1') {
    if (nodes.some(frame => frame.type === 'DELEGATECALL' && [frame.context, frame.to, frame.from]
      .some(account => isAddressEqual(account, wrapper)))) fail('direct wrapper delegate unsupported');
  } else {
    if (nodes.some(frame => isAddressEqual(frame.context, profile.admin.account))) fail('unsupported native admin execution');
    if (nodes.some(frame => frame.type === 'DELEGATECALL' && isAddressEqual(frame.context, wrapper)
      && (!isAddressEqual(frame.to, profile.implementation.account) || !isAddressEqual(frame.from, wrapper)))) fail('unexpected wrapper storage-context delegate');
  }
  function executionSlot(slot: Hex, expected?: bigint) {
    const traced = diffBalance(diff, wrapper, slot, wrapperPre, 'wrapper storage', true);
    if (expected !== undefined && (traced.before !== expected || traced.after !== expected)) fail('native wrapper storage mismatch');
    return traced;
  }
  function executionMetadata(slot: Hex, expected: bigint) {
    if (!wrapperPre.storage.has(slot)) {
      if ([diff.pre, diff.post].some(accounts => {
        const account = accounts.get(wrapper.toLowerCase()); return account && storageMap(account, 'wrapper metadata', true).has(slot);
      })) fail('in-transaction wrapper metadata change without prestate');
      // An unaccessed slot is NOT zero and cannot be reconstructed from block
      // end. Later transactions may change it after this selected operation.
      return { scope: 'unaccessed-transaction-metadata' as const, value: null };
    }
    return { scope: 'transaction-prestate-and-diff' as const, value: executionSlot(slot, expected) };
  }
  // Immutables/source are established from selected-transaction PRESTATE code,
  // never compatible getters, block-end code, caller offsets or a proxy fiction.
  const executionWrapper = profile.profile === 'reviewed-solmate-direct-weth-v1' ? {
    executionWrapperMetadata: { kind: 'solmate-direct-weth-v1' as const,
      scope: 'transaction-prestate-and-diff' as const, sourceFamily: profile.sourceFamily,
      immutableReferences: assertDirectNativeWrapperRuntime(executionCodes.get(wrapper.toLowerCase())!, profile) },
  } : {
    executionProxy: { implementationWord: executionSlot(nativeImplSlot, BigInt(profile.implementation.account)),
      adminWord: executionSlot(nativeAdminSlot, BigInt(profile.admin.account)) },
    executionWrapperMetadata: { l2Gateway: executionMetadata(normalizedSlot('0xcc', 'gateway slot'), BigInt(profile.expectedL2Gateway)),
      l1Address: executionMetadata(normalizedSlot('0xcd', 'l1 slot'), BigInt(profile.expectedL1Address)),
      decimals: executionMetadata(normalizedSlot('0x38', 'decimals slot'), 18n) },
  };
  const supply = executionSlot(normalizedSlot(direct ? '0x02' : '0x35', 'supply slot'));
  const wrappedRouter = executionSlot(derivedBalanceSlot(router, direct ? '3' : '51'));
  const wrappedPoolBalance = executionSlot(derivedBalanceSlot(pool, direct ? '3' : '51'));
  const tokenBalance = (holder: Address, label: string) => diffBalance(diff, endpoint.token,
    derivedBalanceSlot(holder, endpoint.balanceMappingSlot), tokenPre, label, true);
  const tokenVault = tokenBalance(vault, 'endpoint vault');
  const tokenPoolBalance = tokenBalance(pool, 'endpoint pool');
  const tokenRouter = buy ? tokenBalance(router, 'endpoint router') : null;
  const tokenTreasury = buy ? tokenBalance(treasury, 'endpoint treasury') : null;
  const nativeRouter = nativeBalance(router, 'router');
  const nativeWrapper = nativeBalance(wrapper, 'wrapper'), nativeTreasury = buy ? null : nativeBalance(treasury, 'treasury');
  if (nativeWrapper.before < supply.before || nativeWrapper.after < supply.after) fail('native wrapper underbacked');
  // Canonical state methods observe BLOCK END, not this transaction's poststate.
  // Preserve these as separate compatibility responses; never use their mutable
  // values to accept/reject or reconstruct the selected transaction cashflows.
  async function blockEndRead<T>(read: () => Promise<T>) {
    try { return { status: 'observed-at-block-end' as const, value: await read() }; }
    catch { return { status: 'block-end-response-unavailable' as const, value: null }; }
  }
  const blockEndObservation = await (async () => {
    if (profile.profile === 'reviewed-solmate-direct-weth-v1') {
      const [nativeCollateral, decimals, totalSupply] = await Promise.all([
        blockEndRead(() => state.getBalance({ address: wrapper, blockNumber: source.number })),
        blockEndRead(() => state.readContract({ address: wrapper, abi: directNativeWrapperObservationAbi, functionName: 'decimals', blockNumber: source.number })),
        blockEndRead(() => state.readContract({ address: wrapper, abi: directNativeWrapperObservationAbi, functionName: 'totalSupply', blockNumber: source.number })),
      ]);
      return { kind: 'solmate-direct-weth-v1' as const,
        scope: 'canonical-block-end-not-selected-transaction-poststate' as const,
        nativeCollateral, decimals, totalSupply, transactionContinuityProven: false as const };
    }
    const [adminCode, blockEndImplementation, blockEndAdmin, blockEndCollateral,
      blockEndDecimals, blockEndSupply, blockEndGateway, blockEndL1Address] = await Promise.all([
      blockEndRead(() => state.getBytecode({ address: profile.admin.account, blockNumber: source.number })),
      blockEndRead(() => state.getStorageAt({ address: wrapper, slot: nativeImplSlot, blockNumber: source.number })),
      blockEndRead(() => state.getStorageAt({ address: wrapper, slot: nativeAdminSlot, blockNumber: source.number })),
      blockEndRead(() => state.getBalance({ address: wrapper, blockNumber: source.number })),
      blockEndRead(() => state.readContract({ address: wrapper, abi: nativeWrapperObservationAbi, functionName: 'decimals', blockNumber: source.number })),
      blockEndRead(() => state.readContract({ address: wrapper, abi: nativeWrapperObservationAbi, functionName: 'totalSupply', blockNumber: source.number })),
      blockEndRead(() => state.readContract({ address: wrapper, abi: nativeWrapperObservationAbi, functionName: 'l2Gateway', blockNumber: source.number })),
      blockEndRead(() => state.readContract({ address: wrapper, abi: nativeWrapperObservationAbi, functionName: 'l1Address', blockNumber: source.number })),
    ]);
    const adminRuntimeHash = adminCode.status === 'observed-at-block-end' ? keccak256(adminCode.value ?? '0x') : null;
    return { scope: 'canonical-block-end-not-selected-transaction-poststate' as const,
      adminRuntimeHash, adminRuntimeMatchesReviewedPin: adminRuntimeHash === null ? null : adminRuntimeHash === profile.admin.codeHash,
      implementationWord: blockEndImplementation, adminWord: blockEndAdmin, nativeCollateral: blockEndCollateral,
      decimals: blockEndDecimals, totalSupply: blockEndSupply, l2Gateway: blockEndGateway, l1Address: blockEndL1Address,
      transactionContinuityProven: false as const, upgradeAuthorityVerified: false as const };
  })();

  const contamination = buy ? tokenRouter!.before !== 0n : wrappedRouter.before !== 0n || nativeRouter.before !== 0n;
  if (contamination) {
    await finalSource();
    return Object.freeze({ schemaVersion: 1 as const, scope, accountDeployment,
      source: { ...identity, timestamp, traceCommitments: commitments, traceNotCryptographicProof: true as const },
      owner, profile, endpointProfile: endpoint, ...executionWrapper,
      blockEndObservation, routerPrebalanceContamination: true as const,
      outcome: 'unavailable' as const, reason: 'router-prebalance-contamination' as const, trade: null,
      nativePrincipalDelta: null, gasReconciliation: null, verifiedEntryPointPrefundPaid: owner.prefundPaid,
      userOperationActualGasCost: segment.actualGasCost, entryPointDepositReconciliationVerified: false as const, vaultGasDebitConserved: false as const,
      balances: { nativeVault, nativeRouter, nativeWrapper, entryPointNative, nativeTreasury, wrappedRouter, wrappedPoolBalance, supply, tokenVault, tokenPoolBalance, tokenRouter, tokenTreasury },
      traceBounds: { maxBytesPerTrace: MAX_TRACE_BYTES, maxFrames: MAX_TRACE_FRAMES, observedFrames: owner.evidence.traceBounds.observedFrames, actualDebugRequests: cache.size },
      ...legacyAuthority, submissionAuthorized: false as const, ...flags });
  }

  const logs = segment.candidateLogs;
  const transfers = (token: Address) => logs.filter(log => isAddressEqual(log.address, token)
    && log.topics[0]?.toLowerCase() === encodeEventTopics({ abi: nativeTransferAbi, eventName: 'Transfer' })[0]).map(log => {
    const args = decodeEventLog({ abi: nativeTransferAbi, eventName: 'Transfer', topics: log.topics, data: log.data, strict: true }).args;
    if (canonical(log.topics) !== canonical(encodeEventTopics({ abi: nativeTransferAbi, eventName: 'Transfer', args: { from: args.from, to: args.to } }))
      || log.data !== encodeAbiParameters(parseAbiParameters('uint256'), [args.value])) fail('noncanonical native Transfer');
    return { ...args, index: safeIndex(log.logIndex, 'native Transfer index') };
  });
  const wrappedLogs = transfers(wrapper), tokenLogs = transfers(endpoint.token);
  const exactTransfer = (entries: typeof wrappedLogs, from: Address, to: Address) => {
    const found = entries.filter(log => isAddressEqual(log.from, from) && isAddressEqual(log.to, to));
    if (found.length !== 1) fail('exact native transfer required'); return found[0]!;
  };
  const swapLogs = logs.filter(log => isAddressEqual(log.address, pool)
    && log.topics[0]?.toLowerCase() === encodeEventTopics({ abi: nativeSwapAbi, eventName: 'Swap' })[0]);
  if (swapLogs.length !== 1) fail('exact native pool Swap required');
  const swapLog = swapLogs[0]!, swapArgs = decodeEventLog({ abi: nativeSwapAbi, eventName: 'Swap', topics: swapLog.topics, data: swapLog.data, strict: true }).args;
  if (canonical(swapLog.topics) !== canonical(encodeEventTopics({ abi: nativeSwapAbi, eventName: 'Swap', args: { sender: swapArgs.sender, to: swapArgs.to } }))
    || swapLog.data !== encodeAbiParameters(parseAbiParameters('uint256,uint256,uint256,uint256'),
      [swapArgs.amount0In, swapArgs.amount1In, swapArgs.amount0Out, swapArgs.amount1Out])
    || !isAddressEqual(swapArgs.sender, router) || !isAddressEqual(swapArgs.to, router)) fail('native pool Swap mismatch');
  const input0 = BigInt(decoded.route.tokens[0]!) < BigInt(decoded.route.tokens[1]!);
  const poolInput = input0 ? swapArgs.amount0In : swapArgs.amount1In, gross = input0 ? swapArgs.amount1Out : swapArgs.amount0Out;
  if (poolInput !== swap.amountIn || gross <= 0n || (input0 ? swapArgs.amount1In : swapArgs.amount0In) !== 0n
    || (input0 ? swapArgs.amount0Out : swapArgs.amount1Out) !== 0n) fail('native pool amount mismatch');
  const zero = address(ZERO_ADDRESS, 'zero'), transition = buy ? exactTransfer(wrappedLogs, zero, router) : exactTransfer(wrappedLogs, router, zero);
  const wrappedPool = buy ? exactTransfer(wrappedLogs, router, pool) : exactTransfer(wrappedLogs, pool, router);
  const tokenPool = buy ? exactTransfer(tokenLogs, pool, router) : exactTransfer(tokenLogs, vault, pool);
  const transitionAmount = buy ? swap.amountIn : gross;
  if (wrappedLogs.length !== 2 || transition.value !== transitionAmount || wrappedPool.value !== transitionAmount
    || tokenPool.value !== (buy ? gross : swap.amountIn)
    || tokenPool.index >= swapLog.logIndex! || wrappedPool.index >= swapLog.logIndex!
    || buy && transition.index >= wrappedPool.index || !buy && transition.index <= swapLog.logIndex!) fail('native mint/burn/input/output ordering mismatch');

  const directTransitionLog = direct ? (() => {
    const sourceLogs = logs.filter(log => isAddressEqual(log.address, wrapper));
    const eventTopic = encodeEventTopics({ abi: directWrapperEventsAbi, eventName: buy ? 'Deposit' : 'Withdrawal' })[0];
    const events = sourceLogs.filter(log => log.topics[0]?.toLowerCase() === eventTopic);
    if (sourceLogs.length !== 3 || events.length !== 1) fail('exact direct wrapper Deposit/Withdrawal required');
    const event = events[0]!;
    const args = buy ? decodeEventLog({ abi: directWrapperEventsAbi, eventName: 'Deposit', topics: event.topics, data: event.data, strict: true }).args
      : decodeEventLog({ abi: directWrapperEventsAbi, eventName: 'Withdrawal', topics: event.topics, data: event.data, strict: true }).args;
    const caller = 'from' in args ? args.from : args.to;
    const expectedTopics = buy ? encodeEventTopics({ abi: directWrapperEventsAbi, eventName: 'Deposit', args: { from: router } })
      : encodeEventTopics({ abi: directWrapperEventsAbi, eventName: 'Withdrawal', args: { to: router } });
    const eventIndex = safeIndex(event.logIndex, 'direct wrapper event index');
    if (!isAddressEqual(caller, router) || args.amount !== transitionAmount
      || canonical(event.topics) !== canonical(expectedTopics)
      || event.data !== encodeAbiParameters(parseAbiParameters('uint256'), [transitionAmount])
      || eventIndex !== transition.index + 1 || buy && eventIndex >= wrappedPool.index) fail('direct wrapper source event/order mismatch');
    return event;
  })() : null;

  const routerCall = nativeTrade.calls.at(-1)!;
  const routers = nodes.filter(frame => frame.type === 'CALL' && isAddressEqual(frame.from, vault) && isAddressEqual(frame.to, router)
    && frame.input === routerCall.data && frame.value === routerCall.value);
  if (routers.length !== 1) fail('native signed router CALL missing');
  const routerFrame = routers[0]!;
  const poolData = encodeFunctionData({ abi: nativePoolCallsAbi, functionName: 'swap',
    args: [input0 ? 0n : gross, input0 ? gross : 0n, router, '0x'] });
  const poolFrames = routerFrame.calls.filter(frame => frame.type === 'CALL' && isAddressEqual(frame.from, router)
    && isAddressEqual(frame.to, pool) && frame.input === poolData && frame.value === 0n);
  if (poolFrames.length !== 1) fail('actual native pool swap CALL missing');
  const poolFrame = poolFrames[0]!;
  const transitionData = buy ? encodeFunctionData({ abi: nativeWrapperCallsAbi, functionName: 'deposit' })
    : encodeFunctionData({ abi: nativeWrapperCallsAbi, functionName: 'withdraw', args: [gross] });
  const transitions = routerFrame.calls.filter(frame => frame.type === 'CALL' && isAddressEqual(frame.to, wrapper)
    && isAddressEqual(frame.from, router) && frame.input === transitionData && frame.value === (buy ? swap.amountIn : 0n));
  if (transitions.length !== 1) fail('actual wrapper deposit/withdraw CALL missing');
  const wrapperCall = transitions[0]!;
  let transitionBody = wrapperCall;
  if (profile.profile === 'reviewed-solmate-direct-weth-v1') {
    if (wrapperCall.output !== '0x') fail('direct wrapper transition output mismatch');
    const canonicalLog = (value: unknown) => {
      const log = ownObject(value, 'direct wrapper log');
      return canonical({ address: address(log.address, 'direct wrapper log emitter').toLowerCase(),
        data: hex(log.data, 'direct wrapper log data'),
        topics: Array.isArray(log.topics) ? log.topics.map(topic => hash(topic, 'direct wrapper log topic')) : fail('direct wrapper log topics required') });
    };
    const transferLog = logs.find(log => log.logIndex === transition.index)!;
    if (canonical(wrapperCall.logs.map(canonicalLog)) !== canonical([transferLog, directTransitionLog].map(canonicalLog))) {
      fail('direct wrapper transition trace log/order mismatch');
    }
  } else {
    const delegate = wrapperCall.calls[0];
    if (wrapperCall.calls.length !== 1 || !delegate || delegate.type !== 'DELEGATECALL'
      || !isAddressEqual(delegate.from, wrapper) || !isAddressEqual(delegate.to, profile.implementation.account)
      || delegate.input !== transitionData || delegate.output !== wrapperCall.output
      || delegate.valuePresent && delegate.value !== wrapperCall.value) fail('actual wrapper proxy delegate mismatch');
    transitionBody = delegate;
  }
  const wrappedTransferData = encodeFunctionData({ abi: nativeWrapperCallsAbi, functionName: 'transfer',
    args: [buy ? pool : router, transitionAmount] });
  const wrappedTransfers = nodes.filter(frame => frame.type === 'CALL' && isAddressEqual(frame.to, wrapper)
    && isAddressEqual(frame.from, buy ? router : pool) && frame.parent === (buy ? routerFrame : poolFrame)
    && frame.input === wrappedTransferData && frame.value === 0n);
  if (wrappedTransfers.length !== 1 || routerFrame.calls.indexOf(wrapperCall) === -1
    || (buy ? routerFrame.calls.indexOf(wrapperCall) >= routerFrame.calls.indexOf(poolFrame)
      : routerFrame.calls.indexOf(wrapperCall) <= routerFrame.calls.indexOf(poolFrame))) fail('native transition/pool ordering mismatch');
  if (direct && (wrappedTransfers[0]!.calls.length !== 0
    || wrappedTransfers[0]!.output !== encodeAbiParameters(parseAbiParameters('bool'), [true]))) fail('direct wrapper transfer call/output mismatch');
  for (const frame of nodes.filter(frame => isAddressEqual(frame.to, wrapper) && frame.type !== 'DELEGATECALL')) {
    if (frame === wrapperCall || frame === wrappedTransfers[0]) continue;
    const read = decodeFunctionData({ abi: nativeWrapperCallsAbi, data: frame.input });
    if (frame.type !== 'STATICCALL' || read.functionName !== 'balanceOf'
      || encodeFunctionData({ abi: nativeWrapperCallsAbi, functionName: read.functionName, args: read.args }) !== frame.input
      || frame.value !== 0n) fail('unexpected wrapper management/call route');
  }
  const expectedLogs = logs.filter(log => [wrapper, endpoint.token, pool].some(account => isAddressEqual(account, log.address)))
    .map(log => canonical({ address: log.address.toLowerCase(), data: log.data.toLowerCase(), topics: log.topics.map(topic => topic.toLowerCase()) })).sort();
  const tracedLogs = nodes.flatMap(frame => frame.logs).filter(log => [wrapper, endpoint.token, pool].some(account => isAddressEqual(account, address(log.address, 'trace log emitter'))))
    .map(log => canonical({ address: address(log.address, 'trace log emitter').toLowerCase(), data: hex(log.data, 'trace log data'),
      topics: Array.isArray(log.topics) ? log.topics.map(topic => hash(topic, 'trace log topic')) : fail('trace topics required') })).sort();
  if (canonical(expectedLogs) !== canonical(tracedLogs)) fail('native receipt/trace log mismatch');
  const payouts = nodes.filter(frame => frame.type === 'CALL' && frame.value !== 0n);
  const fee = gross * BigInt(swap.feeBps) / 10_000n, net = gross - fee;
  const allowedValues = new Set<NativeFrame>();
  const take = (from: Address, to: Address, value: bigint, parent?: NativeFrame, empty = true) => {
    const found = nodes.filter(frame => frame.type === 'CALL' && isAddressEqual(frame.from, from) && isAddressEqual(frame.to, to)
      && frame.value === value && (!parent || frame.parent === parent) && (!empty || frame.input === '0x') && !allowedValues.has(frame));
    if (found.length !== 1) fail('exact native cashflow CALL required'); allowedValues.add(found[0]!); return found[0]!;
  };
  if (owner.prefundPaid !== 0n) take(vault, identity.entryPoint, owner.prefundPaid);
  const handle = decodeFunctionData({ abi: entryPoint07Abi, data: before.transaction.input });
  if (handle.functionName !== 'handleOps') fail('handleOps required');
  if (segment.actualGasCost !== 0n) take(identity.entryPoint, handle.args[1], segment.actualGasCost);
  if (buy) {
    allowedValues.add(routerFrame); allowedValues.add(wrapperCall);
    if (transitionBody.calls.length !== 0) fail('unexpected deposit implementation external call');
  } else {
    const unwrap = take(wrapper, router, gross, transitionBody);
    if (unwrap.calls.length !== 0 || transitionBody.calls.length !== 1) fail('unexpected withdraw payout route');
    const nativeFee = take(router, treasury, fee, routerFrame), nativeNet = take(router, vault, net, routerFrame);
    if (routerFrame.calls.indexOf(nativeFee) >= routerFrame.calls.indexOf(nativeNet)) fail('native fee/sweep order mismatch');
  }
  if (payouts.some(frame => !allowedValues.has(frame))) fail('unexpected native value transfer');
  // Whole-balance commands consume donations. Keep canonical inclusion, but
  // never attribute those funds as this trade's fee/output or net balance.
  if (!contamination) {
    if (supply.delta !== (buy ? swap.amountIn : -gross) || nativeWrapper.delta !== supply.delta
      || wrappedPoolBalance.delta !== (buy ? swap.amountIn : -gross) || tokenPoolBalance.delta !== (buy ? -gross : swap.amountIn)
      || wrappedRouter.after !== wrappedRouter.before || nativeRouter.after !== nativeRouter.before
      || tokenRouter && tokenRouter.after !== 0n || tokenVault.delta !== (buy ? net + (isAddressEqual(treasury, vault) ? fee : 0n) : -swap.amountIn)
      || buy && !isAddressEqual(treasury, vault) && tokenTreasury!.delta !== fee
      || !buy && !isAddressEqual(treasury, vault) && nativeTreasury!.delta !== fee
      || nativeVault.delta !== (buy ? -swap.amountIn : net + (isAddressEqual(treasury, vault) ? fee : 0n)) - owner.prefundPaid
      || entryPointNative.delta !== owner.prefundPaid - segment.actualGasCost) fail('native principal/prefund/backing conservation mismatch');
    if (buy) {
      const distributions = tokenLogs.filter(log => isAddressEqual(log.from, router));
      if (tokenLogs.length !== 3 || distributions.length !== 2 || distributions[0]!.value !== fee || distributions[1]!.value !== net
        || !isAddressEqual(distributions[0]!.to, treasury) || !isAddressEqual(distributions[1]!.to, vault)
        || distributions[0]!.index <= swapLog.logIndex! || distributions[1]!.index <= distributions[0]!.index) fail('native BUY ERC20 fee/sweep mismatch');
    } else if (tokenLogs.length !== 1) fail('unexpected native SELL input transfer');
    if (net < BigInt(nativeTrade.quote.minimumAmountOut)) fail('native output below reviewed minimum');
  }
  await finalSource();
  const nativePrincipalDelta = buy ? -swap.amountIn : net;
  const gasReconciliation = reconcileGas(nativePrincipalDelta, !buy && isAddressEqual(treasury, vault) ? fee : 0n);
  return Object.freeze({ schemaVersion: 1 as const, scope, accountDeployment,
    source: { ...identity, timestamp, traceCommitments: commitments, traceNotCryptographicProof: true as const },
    owner, profile, endpointProfile: endpoint, ...executionWrapper,
    blockEndObservation, routerPrebalanceContamination: contamination,
    outcome: contamination ? 'unavailable' as const : 'observed' as const,
    reason: contamination ? 'router-prebalance-contamination' as const : null,
    trade: contamination ? null : { amountIn: swap.amountIn, swapGrossOutput: gross, terminalFeeCredit: fee, vaultNetCredit: net,
      tokenIn: swap.tokenIn, tokenOut: swap.tokenOut, feeBps: swap.feeBps, transferSemanticsVerified: false as const, balanceAccountingPublished: false as const },
    nativePrincipalDelta, gasReconciliation,
    verifiedEntryPointPrefundPaid: owner.prefundPaid, userOperationActualGasCost: segment.actualGasCost,
    entryPointDepositReconciliationVerified: true as const, vaultGasDebitConserved: true as const,
    balances: { nativeVault, nativeRouter, nativeWrapper, entryPointNative, nativeTreasury, wrappedRouter, wrappedPoolBalance, supply, tokenVault, tokenPoolBalance, tokenRouter, tokenTreasury },
    traceBounds: { maxBytesPerTrace: MAX_TRACE_BYTES, maxFrames: MAX_TRACE_FRAMES, observedFrames: redacted.frames, actualDebugRequests: cache.size },
    ...legacyAuthority, submissionAuthorized: false as const, ...flags });
}
